import React, {
  useCallback, useMemo, useRef, useState, useEffect,
} from 'react';

import { geojsonToWKT } from '@terraformer/wkt';
import { GeoLocation, GeoLocationMarker } from '@vlabs/uikit';
import { Button } from '@vlabs/uikit/components/controls/button';
import { ZoomControl } from 'pigeon-maps';

import { RegionsDrawing } from '@vlabs/monokit/shared/ui/regions-drawing';
import type { TRegionColors } from '@vlabs/monokit/shared/ui/regions-drawing';

import { wktToLatLng } from '../converters';
import st from './EditDepartmentGeoZoneForm.module.sass';
import { calculatePolygonCenter } from '../calculatePolygonCenter';
import { useEditDepartmentGeoZoneTranslation } from '../i18n';
import type {
  TPixel, TSize, TDrawingRegion, TRegionChange, TLatLng, TPigeonMap, TBoundsChange, TGeoMapProps,
} from '../types';
import { EditDepartmentGeoZoneControlPanel } from './EditDepartmentGeoZoneControlPanel';

const ZONE_REGION_ID = 'geo-zone';
const MIN_POLYGON_POINTS = 3;
const DEFAULT_MAP_CENTER: [number, number] = [25.2298, 55.2893]; // Dubai

export interface EditDepartmentGeoZoneFormValues {
  zoneCoordinates?: string;
}

interface EditDepartmentGeoZoneFormProps {
  defaultValue?: string | null;
  onSubmit: (values: EditDepartmentGeoZoneFormValues) => void;
  onCancel?: VoidFunction;
}

function EditDepartmentGeoZoneForm({
  defaultValue = undefined,
  onSubmit,
  onCancel = undefined,
}: EditDepartmentGeoZoneFormProps): React.JSX.Element {
  const { t } = useEditDepartmentGeoZoneTranslation();
  const [colorValue, setColorValue] = useState<TRegionColors>('accentBlue');
  const [activeRegion, setActiveRegion] = useState<TDrawingRegion | undefined>(undefined);

  const [coords, setCoords] = useState<TPixel[]>([]);

  const [mapCenter, setMapCenter] = useState<TLatLng>(DEFAULT_MAP_CENTER);
  const [mapZoom, setMapZoom] = useState<number>(12);

  const [isDrawing, setIsDrawing] = useState(false);
  const [mediaOriginSize, setMediaOriginSize] = useState<TSize>({ width: 0, height: 0 });

  const observerRef = useRef<ResizeObserver | null>(null);
  const mapRef = useRef<TPigeonMap | null>(null);
  const geoCoordsRef = useRef<TLatLng[]>(wktToLatLng(defaultValue));
  const isGetLocationPending = useRef<boolean>(true);

  useEffect(() => {
    const getLocation = () => {
      if (defaultValue) {
        const points = wktToLatLng(defaultValue);
        if (points.length > 0) {
          const center = calculatePolygonCenter(points);
          setMapCenter(center);
          isGetLocationPending.current = false;
          return;
        }
      }

      if (!navigator.geolocation) {
        setMapCenter(DEFAULT_MAP_CENTER);
        isGetLocationPending.current = false;
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          setMapCenter([position.coords.latitude, position.coords.longitude]);
        },
        () => {
          setMapCenter(DEFAULT_MAP_CENTER);
        },
      );

      isGetLocationPending.current = false;
    };

    getLocation();
  }, [defaultValue]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setIsDrawing(coords.length < MIN_POLYGON_POINTS);
      if ((e.key === 'Backspace' || e.key === 'Delete') && activeRegion) {
        geoCoordsRef.current = [];
        setCoords([]);
        setActiveRegion(undefined);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setIsDrawing(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [activeRegion, coords]);

  const recalcCoords = useCallback((center?: TLatLng, zoom?: number): void => {
    const map = mapRef.current;
    if (!map) return;
    setCoords(geoCoordsRef.current.map((latLng) => {
      const [x, y] = map.latLngToPixel(latLng, center, zoom);
      return { x, y };
    }));
  }, []);

  const handleBoundsChanged = useCallback(({ center, zoom }: TBoundsChange): void => {
    if (isGetLocationPending.current) return;
    setMapCenter(center);
    setMapZoom(zoom);
    recalcCoords(center, zoom);
  }, [recalcCoords, setMapCenter, setMapZoom]);

  const mapProps = useMemo(
    () => ({
      ref: mapRef,
      onBoundsChanged: handleBoundsChanged,
      center: mapCenter,
      zoom: mapZoom,
    }) as unknown as TGeoMapProps,
    [handleBoundsChanged, mapCenter, mapZoom],
  );

  const mapWrapperRef = useCallback((node: HTMLDivElement | null): void => {
    observerRef.current?.disconnect();

    if (!node) {
      observerRef.current = null;
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setMediaOriginSize({ width, height });
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  const handleChangeRegion = (region: TRegionChange): void => {
    const next = region.coords ?? [];
    setCoords(next);

    const map = mapRef.current;
    if (map) geoCoordsRef.current = next.map(({ x, y }) => map.pixelToLatLng([x, y]));

    if (next.length >= MIN_POLYGON_POINTS) setIsDrawing(false);
  };

  const isComplete = coords.length >= MIN_POLYGON_POINTS;

  const region: TDrawingRegion = {
    id: ZONE_REGION_ID,
    form: 'wkt',
    mode: 'abs',
    figure: 'polygon',
    isVisible: true,
    color: colorValue,
    coords,
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    const map = mapRef.current;
    const [first] = coords;
    if (!isComplete || !map || !first) return;

    const ring = [...coords, first].map(({ x, y }) => {
      const [lat, lng] = map.pixelToLatLng([x, y]);
      // при возврате меняем [lat, lng] => [lng, lat], т.к. в экосистеме форматов WKT/GeoJSON принят порядок координат "lng, lat"
      return [lng, lat];
    });
    const zoneCoordinates = geojsonToWKT({ type: 'Polygon', coordinates: [ring] }) as string;

    onSubmit({ zoneCoordinates });
  };

  return (
    <form
      className={st.Form}
      data-testid="EditDepartmentGeoZoneForm"
      onSubmit={handleSubmit}
    >
      <h5>{t('title')}</h5>

      <div
        className={st.MapWrapper}
        ref={mapWrapperRef}
      >
        <GeoLocation
          mapProps={mapProps}
          toggleFullScreenMode={false}
        >
          <GeoLocationMarker
            anchor={mapCenter}
            className={st.HiddenMarker}
          />
          <RegionsDrawing
            activeRegion={activeRegion}
            className={st.DrawingOverlay}
            isDrawing={isDrawing}
            mediaOriginSize={mediaOriginSize}
            onChangeActiveRegion={(selectedRegion) => {
              setActiveRegion(selectedRegion as TDrawingRegion);
            }}
            onChangeRegion={handleChangeRegion}
            regionsList={[region]}
          />
          <ZoomControl
            buttonStyle={{
              width: 40,
              height: 40,
            }}
            style={{
              zIndex: 1,
              top: '50%',
              right: 12,
              left: 'none',
              transform: 'translateY(-50%)',
            }}
          />
        </GeoLocation>
      </div>

      <EditDepartmentGeoZoneControlPanel
        colorValue={colorValue}
        onChangeColor={(color) => setColorValue(color)}
      />

      <div className={st.Actions}>
        <Button
          disabled={!isComplete}
          type="submit"
        >
          {t('submit')}
        </Button>

        {!!onCancel && (
          <Button
            kind="secondary"
            onClick={onCancel}
            type="button"
            variant="outlined"
          >
            {t('cancel')}
          </Button>
        )}
      </div>
    </form>
  );
}

EditDepartmentGeoZoneForm.displayName = 'EditDepartmentGeoZoneForm';

export { EditDepartmentGeoZoneForm };
